import React from 'react'
import PhoneInput from 'react-phone-number-input'
import type { Value as PhoneValue } from 'react-phone-number-input'
import 'react-phone-number-input/style.css'
import { apiDelete, apiGet, apiPost, apiPut, ContactLimitError } from '../../../lib/api'
import { CONTACT, CONTACT_VISIT, CONTACT_VISITS, CONTACTS, SERVICES } from '../../../lib/endpoints'
import { loadAuth } from '../../../lib/authStorage'
import type { Contact, ContactVisit, Service } from '../../../types'
import { normalizeDateInput, lastVisitDateForInput, visitsFieldError } from '../utils'
import { useDashboard } from '../DashboardContext'
import { UpgradePrompt } from './UpgradePrompt'
import { LogVisitModal } from './LogVisitModal'

export type ContactWithStatus = Contact & { isLapsed?: boolean }

type ContactForm = {
  fullName: string
  email: string
  phone: string
  lastVisitDate: string
  visits: string
  tag: string
}

type MetadataPair = { key: string; value: string }

type VisitItemForm = {
  key: number
  serviceId: string
  quantity: string
  unitPrice: string
}

type VisitRow = {
  _key: string
  date: string
  items: VisitItemForm[]
  totalEdited: boolean
  staffName: string
  totalAmount: string
  notes: string
}

let _visitItemKeySeq = 0
function emptyVisitItem(): VisitItemForm {
  return { key: ++_visitItemKeySeq, serviceId: '', quantity: '1', unitPrice: '' }
}

function autoVisitTotal(items: VisitItemForm[]): number {
  let sum = 0
  for (const it of items) {
    const price = Number(it.unitPrice)
    const qty = Number(it.quantity || '1')
    if (Number.isFinite(price) && price > 0 && Number.isFinite(qty) && qty > 0) {
      sum += price * qty
    }
  }
  return sum
}

function newVisitRow(date = ''): VisitRow {
  return { _key: Math.random().toString(36).slice(2), date, items: [emptyVisitItem()], totalEdited: false, staffName: '', totalAmount: '', notes: '' }
}

function todayStr(): string {
  return new Date().toLocaleDateString('sv-SE')
}

// ─── Add Contact Modal ────────────────────────────────────────────────────────

interface AddContactModalProps {
  open: boolean
  onClose: () => void
  onSaved: () => Promise<void>
}

export function AddContactModal({ open, onClose, onSaved }: AddContactModalProps) {
  const { setActivePage } = useDashboard()
  const [busy, setBusy] = React.useState(false)
  const [error, setError] = React.useState<string | null>(null)
  const [partialSuccess, setPartialSuccess] = React.useState(false)
  const [limitError, setLimitError] = React.useState<ContactLimitError | null>(null)
  const [fullName, setFullName] = React.useState('')
  const [email, setEmail] = React.useState('')
  const [phone, setPhone] = React.useState<PhoneValue | undefined>(undefined)
  const [tag, setTag] = React.useState('')
  const [visitRows, setVisitRows] = React.useState<VisitRow[]>([newVisitRow(todayStr())])
  const [metadata, setMetadata] = React.useState<MetadataPair[]>([{ key: '', value: '' }])
  const [services, setServices] = React.useState<Service[]>([])

  React.useEffect(() => {
    if (!open) return
    apiGet<Service[]>(SERVICES)
      .then(data => setServices(data.filter(s => s.active)))
      .catch(() => setServices([]))
  }, [open])

  function resetForm() {
    setFullName('')
    setEmail('')
    setPhone(undefined)
    setTag('')
    setVisitRows([newVisitRow(todayStr())])
    setMetadata([{ key: '', value: '' }])
    setError(null)
    setLimitError(null)
    setPartialSuccess(false)
  }

  function updateRow(idx: number, patch: Partial<VisitRow>) {
    setVisitRows(rows => rows.map((r, i) => i === idx ? { ...r, ...patch } : r))
  }

  function updateVisitItem(visitIdx: number, itemKey: number, patch: Partial<VisitItemForm>) {
    setVisitRows(rows => rows.map((r, i) => {
      if (i !== visitIdx) return r
      const items = r.items.map(it => it.key === itemKey ? { ...it, ...patch } : it)
      const next: VisitRow = { ...r, items }
      if (!r.totalEdited) {
        const sum = autoVisitTotal(items)
        next.totalAmount = sum > 0 ? sum.toFixed(2) : ''
      }
      return next
    }))
  }

  function addVisitItem(visitIdx: number) {
    setVisitRows(rows => rows.map((r, i) => i === visitIdx ? { ...r, items: [...r.items, emptyVisitItem()] } : r))
  }

  function removeVisitItem(visitIdx: number, itemKey: number) {
    setVisitRows(rows => rows.map((r, i) => {
      if (i !== visitIdx) return r
      const items = r.items.length === 1 ? [emptyVisitItem()] : r.items.filter(it => it.key !== itemKey)
      const next: VisitRow = { ...r, items }
      if (!r.totalEdited) {
        const sum = autoVisitTotal(items)
        next.totalAmount = sum > 0 ? sum.toFixed(2) : ''
      }
      return next
    }))
  }

  async function handleSubmit() {
    if (!loadAuth()?.accessToken) return
    setError(null)
    setPartialSuccess(false)

    if (!fullName.trim()) { setError('Full name is required.'); return }
    if (!email.trim()) { setError('Email is required.'); return }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim())) {
      setError('Please enter a valid email address.'); return
    }
    if (!visitRows[0].date.trim()) { setError('At least one visit date is required.'); return }
    if (visitRows.some(r => r.date.trim() && !r.items.some(it => it.serviceId))) {
      setError('Please select at least one offering for every visit.'); return
    }

    const validRows = visitRows.filter(r => r.date.trim())
    const lastVisitDate = validRows.reduce((max, r) => r.date > max ? r.date : max, validRows[0].date)

    const metadataPairs = metadata
      .map(p => ({ key: p.key.trim(), value: p.value.trim() }))
      .filter(p => p.key && p.value)
    const metadataObj = metadataPairs.reduce<Record<string, string>>((acc, p) => {
      acc[p.key] = p.value
      return acc
    }, {})

    try {
      setBusy(true)
      setLimitError(null)

      const newContact = await apiPost<Contact, Record<string, unknown>>(CONTACTS, {
        fullName: fullName.trim(),
        email: email.trim(),
        phone: phone || null,
        lastVisitDate,
        visits: String(validRows.length),
        tag: tag.trim() || null,
        metadata: Object.keys(metadataObj).length > 0 ? metadataObj : null,
      })

      const failedVisits: number[] = []
      for (let i = 0; i < validRows.length; i++) {
        const row = validRows[i]
        try {
          const validItems = row.items.filter(it => it.serviceId)
          const items = validItems.map(it => ({
            serviceId: it.serviceId,
            quantity: it.quantity.trim() ? Number(it.quantity) : 1,
            unitPrice: it.unitPrice.trim() ? Number(it.unitPrice) : null,
          }))
          await apiPost<ContactVisit, Record<string, unknown>>(CONTACT_VISITS(newContact.id), {
            visitDate: row.date,
            serviceId: validItems[0]?.serviceId ?? null,
            staffName: row.staffName.trim() || null,
            totalAmount: row.totalAmount.trim() ? Number(row.totalAmount) : null,
            notes: row.notes.trim() || null,
            items,
          })
        } catch {
          failedVisits.push(i + 1)
        }
      }

      await onSaved()

      if (failedVisits.length > 0) {
        setPartialSuccess(true)
        setError(
          `Contact saved, but visit${failedVisits.length > 1 ? 's' : ''} #${failedVisits.join(', #')} could not be logged. You can add them from the contact detail.`
        )
        return
      }

      resetForm()
      onClose()
    } catch (e) {
      if (e instanceof ContactLimitError) {
        setLimitError(e)
      } else {
        setError(e instanceof Error ? e.message : 'Unable to add contact')
      }
    } finally {
      setBusy(false)
    }
  }

  if (!open) return null

  const inputStyle: React.CSSProperties = {
    width: '100%',
    background: 'var(--surface2)',
    border: '1px solid var(--border)',
    borderRadius: 8,
    padding: '9px 12px',
    fontSize: 14,
    color: 'var(--text)',
    outline: 'none',
    boxSizing: 'border-box',
  }
  const labelStyle: React.CSSProperties = {
    fontSize: 13,
    fontWeight: 500,
    color: 'var(--text)',
    marginBottom: 6,
    display: 'block',
  }

  return (
    <div
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(0,0,0,0.45)',
        zIndex: 95,
        display: 'grid',
        placeItems: 'center',
        padding: 24,
      }}
      onClick={() => { if (busy) return; onClose() }}
    >
      <div
        className="panel"
        style={{ width: 'min(680px, 96vw)', padding: 20, maxHeight: '92vh', overflowY: 'auto' }}
        onClick={e => e.stopPropagation()}
      >
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 14 }}>
          <div style={{ fontFamily: 'Manrope, sans-serif', fontSize: 22 }}>Add Contact</div>
          <button className="btn-secondary" onClick={onClose} disabled={busy}>Close</button>
        </div>

        <form onSubmit={e => { e.preventDefault(); void handleSubmit() }}>
          {/* ── Contact fields ── */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 12 }}>
            <div>
              <label style={labelStyle}>Full name</label>
              <input className="form-input" value={fullName} onChange={e => setFullName(e.target.value)} />
            </div>
            <div>
              <label style={labelStyle}>Email</label>
              <input className="form-input" type="email" value={email} onChange={e => setEmail(e.target.value)} />
            </div>
            <div>
              <label style={labelStyle}>Phone</label>
              <PhoneInput
                international
                defaultCountry="US"
                value={phone}
                onChange={setPhone}
                className="form-phone"
              />
            </div>
            <div>
              <label style={labelStyle}>Tag (optional)</label>
              <input className="form-input" value={tag} onChange={e => setTag(e.target.value)} />
            </div>
          </div>

          {/* ── Visits ── */}
          <div style={{ marginBottom: 14 }}>
            <div style={{ fontSize: 11, fontFamily: "'DM Mono',monospace", color: 'var(--text3)', marginBottom: 10 }}>
              VISITS *
            </div>
            {services.length === 0 && (
              <div style={{
                background: 'var(--surface2)',
                border: '1px solid var(--border)',
                borderRadius: 10,
                padding: '12px 14px',
                marginBottom: 10,
                fontSize: 13,
                color: 'var(--text2)',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                gap: 12,
              }}>
                <span>You haven't added any offerings yet — add at least one (a service or a product) before logging a visit.</span>
                <button
                  type="button"
                  className="btn-secondary"
                  style={{ fontSize: 12, padding: '4px 12px', whiteSpace: 'nowrap', flexShrink: 0 }}
                  onClick={() => { onClose(); setActivePage('services') }}
                >
                  Add offerings
                </button>
              </div>
            )}
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
              {visitRows.map((row, idx) => (
                <div
                  key={row._key}
                  style={{
                    background: 'var(--surface2)',
                    border: '1px solid var(--border)',
                    borderRadius: 10,
                    padding: '12px 14px',
                  }}
                >
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
                    <span style={{ fontSize: 12, fontWeight: 600, color: 'var(--text2)' }}>
                      Visit {idx + 1}
                    </span>
                    {visitRows.length > 1 && (
                      <button
                        type="button"
                        className="btn-secondary"
                        style={{ fontSize: 12, padding: '3px 10px' }}
                        onClick={() => setVisitRows(rows => rows.filter((_, i) => i !== idx))}
                      >
                        Remove
                      </button>
                    )}
                  </div>

                  <div style={{ marginBottom: 10 }}>
                    <label style={labelStyle}>Date {idx === 0 ? '*' : ''}</label>
                    <input
                      type="date"
                      style={inputStyle}
                      value={row.date}
                      onChange={e => updateRow(idx, { date: e.target.value })}
                    />
                  </div>

                  <div style={{ marginBottom: 10 }}>
                    <label style={labelStyle}>Items *</label>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                      {row.items.map((it, itemIdx) => {
                        const svc = services.find(s => s.id === it.serviceId) ?? null
                        const isProduct = svc?.offeringType === 'PRODUCT'
                        return (
                          <div
                            key={it.key}
                            style={{
                              display: 'grid',
                              gridTemplateColumns: '1fr 60px 100px auto',
                              gap: 6,
                              alignItems: 'center',
                            }}
                          >
                            <div style={{ position: 'relative' }}>
                              <select
                                style={{ ...inputStyle, appearance: 'none', cursor: services.length === 0 ? 'not-allowed' : 'pointer', paddingRight: 28, opacity: services.length === 0 ? 0.6 : 1 }}
                                value={it.serviceId}
                                onChange={e => {
                                  const next = services.find(s => s.id === e.target.value)
                                  updateVisitItem(idx, it.key, {
                                    serviceId: e.target.value,
                                    unitPrice: it.unitPrice || (next?.price != null ? String(next.price) : ''),
                                  })
                                }}
                                disabled={services.length === 0}
                              >
                                <option value="" disabled>
                                  {services.length === 0 ? 'No offerings — add one first' : (itemIdx === 0 ? 'Select an offering…' : 'Add another…')}
                                </option>
                                {services.map(s => (
                                  <option key={s.id} value={s.id}>
                                    {s.name}{s.offeringType === 'PRODUCT' ? ' (product)' : ''}{s.price != null ? ` — $${s.price.toFixed(2)}` : ''}
                                  </option>
                                ))}
                              </select>
                              <span style={{ position: 'absolute', right: 10, top: '50%', transform: 'translateY(-50%)', pointerEvents: 'none', fontSize: 11, color: 'var(--text2)' }}>▼</span>
                            </div>
                            <input
                              type="number"
                              min={1}
                              step={1}
                              style={inputStyle}
                              value={it.quantity}
                              onChange={e => updateVisitItem(idx, it.key, { quantity: e.target.value })}
                              placeholder="Qty"
                              title={isProduct ? 'How many of this product' : 'Quantity'}
                            />
                            <input
                              type="number"
                              min={0}
                              step="0.01"
                              style={inputStyle}
                              value={it.unitPrice}
                              onChange={e => updateVisitItem(idx, it.key, { unitPrice: e.target.value })}
                              placeholder="Unit price"
                            />
                            <button
                              type="button"
                              className="btn-icon-ghost"
                              onClick={() => removeVisitItem(idx, it.key)}
                              aria-label="Remove item"
                            >
                              ×
                            </button>
                          </div>
                        )
                      })}
                      <button
                        type="button"
                        className="btn-secondary"
                        style={{ fontSize: 12, padding: '5px 10px', alignSelf: 'flex-start' }}
                        onClick={() => addVisitItem(idx)}
                        disabled={services.length === 0}
                      >
                        + Add item
                      </button>
                    </div>
                  </div>

                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginBottom: 10 }}>
                    <div>
                      <label style={labelStyle}>Staff Name</label>
                      <input
                        type="text"
                        maxLength={120}
                        style={inputStyle}
                        value={row.staffName}
                        placeholder="e.g. Sarah"
                        onChange={e => updateRow(idx, { staffName: e.target.value })}
                      />
                    </div>
                    <div>
                      <label style={labelStyle}>Total Amount</label>
                      <input
                        type="number"
                        step="0.01"
                        min="0"
                        style={inputStyle}
                        value={row.totalAmount}
                        placeholder="auto from items"
                        onChange={e => updateRow(idx, { totalAmount: e.target.value, totalEdited: true })}
                      />
                      <div style={{ fontSize: 11, color: 'var(--text3)', marginTop: 3 }}>
                        {row.totalEdited ? 'Custom total' : 'Auto-summed from items'}
                      </div>
                    </div>
                  </div>

                  <div>
                    <label style={labelStyle}>Notes</label>
                    <textarea
                      rows={2}
                      maxLength={500}
                      style={{ ...inputStyle, resize: 'vertical', fontFamily: 'inherit', lineHeight: 1.5 }}
                      value={row.notes}
                      placeholder="Optional notes…"
                      onChange={e => updateRow(idx, { notes: e.target.value })}
                    />
                  </div>
                </div>
              ))}
            </div>
            <button
              type="button"
              className="btn-secondary"
              style={{ marginTop: 8 }}
              onClick={() => setVisitRows(rows => [...rows, newVisitRow()])}
            >
              + Add visit
            </button>
          </div>

          {/* ── Metadata ── */}
          <div style={{ marginBottom: 14 }}>
            <div style={{ fontSize: 11, fontFamily: "'DM Mono',monospace", color: 'var(--text3)', marginBottom: 8 }}>
              ADDITIONAL METADATA (KEY / VALUE)
            </div>
            <div style={{ display: 'grid', gap: 8 }}>
              {metadata.map((pair, idx) => (
                <div key={`meta-${idx}`} style={{ display: 'grid', gridTemplateColumns: '1fr 1fr auto', gap: 8 }}>
                  <input
                    className="form-input"
                    placeholder="Key (e.g. Membership)"
                    value={pair.key}
                    onChange={e => setMetadata(rows => rows.map((r, i) => i === idx ? { ...r, key: e.target.value } : r))}
                  />
                  <input
                    className="form-input"
                    placeholder="Value (e.g. VTP)"
                    value={pair.value}
                    onChange={e => setMetadata(rows => rows.map((r, i) => i === idx ? { ...r, value: e.target.value } : r))}
                  />
                  <button
                    type="button"
                    className="btn-secondary"
                    onClick={() => setMetadata(rows => rows.length > 1 ? rows.filter((_, i) => i !== idx) : [{ key: '', value: '' }])}
                  >
                    Remove
                  </button>
                </div>
              ))}
            </div>
            <button
              type="button"
              className="btn-secondary"
              style={{ marginTop: 8 }}
              onClick={() => setMetadata(rows => [...rows, { key: '', value: '' }])}
            >
              + Add metadata row
            </button>
          </div>

          {error && (
            <div style={{
              background: partialSuccess ? '#d1fae5' : '#fee2e2',
              color: partialSuccess ? '#065f46' : '#b91c1c',
              borderRadius: 8, padding: '10px 14px', fontSize: 13, marginBottom: 12,
            }}>
              {error}
            </div>
          )}
          {limitError && (
            <div style={{ marginBottom: 12 }}>
              <UpgradePrompt
                title="Contact limit reached"
                blurb={`You've used ${limitError.currentUsage} of ${limitError.limit} contacts. Upgrade your plan to add more.`}
              />
            </div>
          )}

          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10, marginTop: 4 }}>
            <button type="button" className="btn-secondary" onClick={partialSuccess ? () => { resetForm(); onClose() } : onClose} disabled={busy}>
              {partialSuccess ? 'Close' : 'Cancel'}
            </button>
            {!partialSuccess && (
              <button className="btn-primary" type="submit" disabled={busy}>
                {busy ? 'Saving...' : 'Save Contact'}
              </button>
            )}
          </div>
        </form>
      </div>
    </div>
  )
}

// ─── Contact Detail / Edit Modal ──────────────────────────────────────────────

interface ContactDetailModalProps {
  contact: ContactWithStatus
  onClose: () => void
  onSaved: (updated: Contact) => void
  onDeleted: () => void
}

export function ContactDetailModal({
  contact,
  onClose,
  onSaved,
  onDeleted,
}: ContactDetailModalProps) {
  const [editing, setEditing] = React.useState(false)
  const [busy, setBusy] = React.useState(false)
  const [error, setError] = React.useState<string | null>(null)
  const [logVisitOpen, setLogVisitOpen] = React.useState(false)
  const [visits, setVisits] = React.useState<ContactVisit[]>([])
  const [visitsLoading, setVisitsLoading] = React.useState(false)
  const [services, setServices] = React.useState<Service[]>([])
  const [editingVisitId, setEditingVisitId] = React.useState<string | null>(null)
  const [editVisitForm, setEditVisitForm] = React.useState({
    date: '', serviceId: '', staffName: '', totalAmount: '', notes: '',
  })
  const [visitBusy, setVisitBusy] = React.useState(false)
  const [visitError, setVisitError] = React.useState<string | null>(null)
  const [editForm, setEditForm] = React.useState<ContactForm>({
    fullName: '',
    email: '',
    phone: '',
    lastVisitDate: '',
    visits: '',
    tag: '',
  })
  const [editPhone, setEditPhone] = React.useState<PhoneValue | undefined>(undefined)
  const [editMetadata, setEditMetadata] = React.useState<MetadataPair[]>([{ key: '', value: '' }])

  React.useEffect(() => {
    setEditing(false)
    setError(null)
  }, [contact.id])

  function loadVisits() {
    setVisitsLoading(true)
    apiGet<ContactVisit[]>(CONTACT_VISITS(contact.id))
      .then(data => setVisits(data))
      .catch(() => setVisits([]))
      .finally(() => setVisitsLoading(false))
  }

  React.useEffect(() => {
    loadVisits()
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [contact.id])

  React.useEffect(() => {
    apiGet<Service[]>(SERVICES)
      .then(data => setServices(data.filter(s => s.active)))
      .catch(() => setServices([]))
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [contact.id])

  function beginEdit() {
    const m = contact.metadata ?? {}
    setEditForm({
      fullName: contact.fullName,
      email: contact.email,
      phone: contact.phone ?? '',
      lastVisitDate: lastVisitDateForInput(contact.lastVisitDate),
      visits: m.visits?.trim() ? m.visits : '1',
      tag: m.tag ?? '',
    })
    setEditPhone((contact.phone as PhoneValue) ?? undefined)
    const extraPairs = Object.entries(m)
      .filter(([k]) => k !== 'visits' && k !== 'tag')
      .map(([key, value]) => ({ key, value: String(value) }))
    setEditMetadata(extraPairs.length > 0 ? extraPairs : [{ key: '', value: '' }])
    setError(null)
    setEditingVisitId(null)
    setVisitError(null)
    setEditing(true)
  }

  async function handleSave() {
    if (!loadAuth()?.accessToken) return
    setError(null)
    const normalizedLastVisitDate = normalizeDateInput(editForm.lastVisitDate)
    const metadataPairs = editMetadata
      .map(p => ({ key: p.key.trim(), value: p.value.trim() }))
      .filter(p => p.key && p.value)
    const metadataObj = metadataPairs.reduce<Record<string, string>>((acc, p) => {
      acc[p.key] = p.value
      return acc
    }, {})
    if (!editForm.fullName.trim()) { setError('Full name is required.'); return }
    if (!editForm.email.trim()) { setError('Email is required.'); return }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(editForm.email.trim())) {
      setError('Please enter a valid email address.'); return
    }
    if (!editForm.lastVisitDate.trim()) { setError('Last visit date is required.'); return }
    if (!normalizedLastVisitDate) {
      setError('Last visit date must be in YYYY-MM-DD or MM/DD/YYYY format.'); return
    }
    const visitsErr = visitsFieldError(editForm.visits)
    if (visitsErr) { setError(visitsErr); return }
    try {
      setBusy(true)
      const updated = await apiPut<Contact, Record<string, unknown>>(CONTACT(contact.id), {
        fullName: editForm.fullName.trim(),
        email: editForm.email.trim(),
        phone: editPhone || null,
        lastVisitDate: normalizedLastVisitDate,
        visits: editForm.visits.trim(),
        tag: editForm.tag.trim() || null,
        metadata: Object.keys(metadataObj).length > 0 ? metadataObj : null,
      })
      setEditing(false)
      onSaved(updated)
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Unable to update contact')
    } finally {
      setBusy(false)
    }
  }

  function startEditVisit(v: ContactVisit) {
    setEditingVisitId(v.id)
    setEditVisitForm({
      date: v.visitDate,
      serviceId: v.serviceId ?? '',
      staffName: v.staffName ?? '',
      totalAmount: v.totalAmount != null ? String(v.totalAmount) : '',
      notes: v.notes ?? '',
    })
    setVisitError(null)
  }

  async function handleVisitSave(visitId: string) {
    if (!editVisitForm.date.trim()) { setVisitError('Visit date is required.'); return }
    if (!editVisitForm.serviceId) { setVisitError('Please select an offering.'); return }
    try {
      setVisitBusy(true)
      setVisitError(null)
      await apiPut<ContactVisit, Record<string, unknown>>(CONTACT_VISIT(contact.id, visitId), {
        visitDate: editVisitForm.date,
        serviceId: editVisitForm.serviceId || null,
        staffName: editVisitForm.staffName.trim() || null,
        totalAmount: editVisitForm.totalAmount.trim() ? Number(editVisitForm.totalAmount) : null,
        notes: editVisitForm.notes.trim() || null,
      })
      setEditingVisitId(null)
      loadVisits()
    } catch (e) {
      setVisitError(e instanceof Error ? e.message : 'Unable to update visit')
    } finally {
      setVisitBusy(false)
    }
  }

  async function handleVisitDelete(visitId: string) {
    if (!window.confirm('Delete this visit?')) return
    try {
      setVisitBusy(true)
      await apiDelete(CONTACT_VISIT(contact.id, visitId))
      if (editingVisitId === visitId) setEditingVisitId(null)
      loadVisits()
    } catch (e) {
      setVisitError(e instanceof Error ? e.message : 'Unable to delete visit')
    } finally {
      setVisitBusy(false)
    }
  }

  async function handleDelete() {
    if (!window.confirm(`Delete ${contact.fullName} from contacts?`)) return
    try {
      await apiDelete(CONTACT(contact.id))
      onDeleted()
    } catch (e) {
      window.alert(e instanceof Error ? e.message : 'Unable to delete contact')
    }
  }

  return (
    <div
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(0,0,0,0.45)',
        zIndex: 95,
        display: 'grid',
        placeItems: 'center',
        padding: 24,
      }}
      onClick={() => { setEditing(false); onClose() }}
    >
      <div
        className="panel"
        style={{
          width: 'min(860px, 96vw)',
          padding: 20,
          maxHeight: '90vh',
          overflowY: 'auto',
        }}
        onClick={e => e.stopPropagation()}
      >
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginBottom: 14,
          }}
        >
          <div style={{ fontFamily: 'Manrope, sans-serif', fontSize: 22 }}>
            {editing ? 'Edit contact' : contact.fullName}
          </div>
          <div style={{ display: 'flex', gap: 8 }}>
            {!editing ? (
              <>
                <button type="button" className="btn-secondary" onClick={() => setLogVisitOpen(true)}>
                  Log Visit
                </button>
                <button type="button" className="btn-primary" onClick={beginEdit}>
                  Edit
                </button>
                <button
                  type="button"
                  className="btn-secondary"
                  onClick={() => { setEditing(false); onClose() }}
                >
                  Close
                </button>
              </>
            ) : (
              <>
                <button
                  type="button"
                  className="btn-secondary"
                  onClick={() => { setEditing(false); setError(null) }}
                  disabled={busy}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className="btn-primary"
                  onClick={() => void handleSave()}
                  disabled={busy}
                >
                  {busy ? 'Saving...' : 'Save changes'}
                </button>
              </>
            )}
          </div>
        </div>

        <LogVisitModal
          open={logVisitOpen}
          contactId={contact.id}
          contactName={contact.fullName}
          onClose={() => setLogVisitOpen(false)}
          onSaved={() => {
            loadVisits()
            apiGet<Contact>(CONTACT(contact.id))
              .then(fresh => onSaved(fresh))
              .catch(() => { /* best-effort — visits already refreshed */ })
          }}
        />

        {!editing ? (
          <>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
              <div
                style={{
                  background: 'var(--surface2)',
                  border: '1px solid var(--border)',
                  borderRadius: 12,
                  padding: 14,
                }}
              >
                <div
                  style={{
                    fontSize: 10,
                    fontFamily: "'DM Mono', monospace",
                    color: 'var(--text3)',
                    marginBottom: 6,
                  }}
                >
                  CONTACT
                </div>
                <div style={{ color: 'var(--text)', fontWeight: 600 }}>{contact.email}</div>
                <div style={{ color: 'var(--text2)', marginTop: 6 }}>{contact.phone || '—'}</div>
                <div style={{ color: 'var(--text2)', marginTop: 6 }}>
                  Last visit:{' '}
                  {new Date(contact.lastVisitDate).toLocaleDateString('en-US', {
                    month: 'short',
                    day: 'numeric',
                    year: 'numeric',
                  })}
                </div>
                <div style={{ marginTop: 10 }}>
                  <span className={`pill ${contact.isLapsed ? 'pill-lapsed' : 'pill-pending'}`}>
                    {contact.isLapsed ? 'Lapsed' : 'Active'}
                  </span>
                </div>
              </div>

              <div
                style={{
                  background: 'var(--surface2)',
                  border: '1px solid var(--border)',
                  borderRadius: 12,
                  padding: 14,
                }}
              >
                <div
                  style={{
                    fontSize: 10,
                    fontFamily: "'DM Mono', monospace",
                    color: 'var(--text3)',
                    marginBottom: 6,
                  }}
                >
                  AI CONTEXT / METADATA
                </div>
                {contact.metadata && Object.keys(contact.metadata).length > 0 ? (
                  <div style={{ display: 'grid', gap: 8 }}>
                    {Object.entries(contact.metadata)
                      .slice(0, 12)
                      .map(([k, v]) => (
                        <div
                          key={k}
                          style={{ display: 'flex', justifyContent: 'space-between', gap: 10 }}
                        >
                          <div
                            style={{
                              fontFamily: "'DM Mono', monospace",
                              fontSize: 12,
                              color: 'var(--text2)',
                            }}
                          >
                            {k}
                          </div>
                          <div
                            style={{
                              fontFamily: "'DM Mono', monospace",
                              fontSize: 12,
                              color: 'var(--text)',
                            }}
                          >
                            {String(v)}
                          </div>
                        </div>
                      ))}
                  </div>
                ) : (
                  <div style={{ color: 'var(--text2)' }}>
                    No extra columns were imported for this contact.
                  </div>
                )}
              </div>
            </div>
            <div style={{ marginTop: 14 }}>
              <div style={{ fontSize: 10, fontFamily: "'DM Mono', monospace", color: 'var(--text3)', marginBottom: 8 }}>
                VISIT HISTORY
              </div>
              {visitsLoading ? (
                <div style={{ fontSize: 13, color: 'var(--text2)' }}>Loading…</div>
              ) : visits.length === 0 ? (
                <div style={{ fontSize: 13, color: 'var(--text2)' }}>No visits logged yet.</div>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                  {[...visits].reverse().map(v => (
                    <div
                      key={v.id}
                      style={{
                        background: 'var(--surface2)',
                        border: '1px solid var(--border)',
                        borderRadius: 10,
                        padding: '10px 14px',
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'flex-start',
                        gap: 12,
                      }}
                    >
                      <div>
                        <div style={{ fontWeight: 500, color: 'var(--text)', fontSize: 14 }}>
                          {new Date(v.visitDate).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                        </div>
                        {v.serviceName && <div style={{ fontSize: 12, color: 'var(--text2)', marginTop: 2 }}>{v.serviceName}</div>}
                        {v.staffName && <div style={{ fontSize: 12, color: 'var(--text2)', marginTop: 2 }}>Staff: {v.staffName}</div>}
                        {v.notes && <div style={{ fontSize: 12, color: 'var(--text2)', marginTop: 4, fontStyle: 'italic' }}>{v.notes}</div>}
                      </div>
                      {v.totalAmount != null && (
                        <div style={{ fontWeight: 600, color: 'var(--text)', fontSize: 14, whiteSpace: 'nowrap' }}>
                          ${v.totalAmount.toFixed(2)}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 14 }}>
              <button className="btn-danger" onClick={() => void handleDelete()}>
                Delete Contact
              </button>
            </div>
          </>
        ) : (
          <form onSubmit={e => { e.preventDefault(); void handleSave() }}>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
              <div className="form-row" style={{ marginBottom: 0 }}>
                <label className="form-label">Full name</label>
                <input
                  className="form-input"
                  value={editForm.fullName}
                  onChange={e => setEditForm(v => ({ ...v, fullName: e.target.value }))}
                />
              </div>
              <div className="form-row" style={{ marginBottom: 0 }}>
                <label className="form-label">Email</label>
                <input
                  className="form-input"
                  type="email"
                  value={editForm.email}
                  onChange={e => setEditForm(v => ({ ...v, email: e.target.value }))}
                />
              </div>
              <div className="form-row" style={{ marginBottom: 0 }}>
                <label className="form-label">Phone</label>
                <PhoneInput
                  international
                  defaultCountry="US"
                  value={editPhone}
                  onChange={setEditPhone}
                  className="form-phone"
                />
              </div>
              <div className="form-row" style={{ marginBottom: 0 }}>
                <label className="form-label">Last visit date</label>
                <input
                  className="form-input"
                  type="date"
                  value={editForm.lastVisitDate}
                  onChange={e => setEditForm(v => ({ ...v, lastVisitDate: e.target.value }))}
                />
              </div>
              <div className="form-row" style={{ marginBottom: 0 }}>
                <label className="form-label">Visits (min 1)</label>
                <input
                  className="form-input"
                  type="number"
                  min={1}
                  step={1}
                  value={editForm.visits}
                  onChange={e => setEditForm(v => ({ ...v, visits: e.target.value }))}
                />
              </div>
              <div className="form-row" style={{ marginBottom: 0 }}>
                <label className="form-label">Tag (optional)</label>
                <input
                  className="form-input"
                  value={editForm.tag}
                  onChange={e => setEditForm(v => ({ ...v, tag: e.target.value }))}
                />
              </div>
            </div>
            <div style={{ marginTop: 14 }}>
              <div
                style={{
                  fontSize: 11,
                  fontFamily: "'DM Mono',monospace",
                  color: 'var(--text3)',
                  marginBottom: 8,
                }}
              >
                ADDITIONAL METADATA (KEY / VALUE)
              </div>
              <div style={{ display: 'grid', gap: 8 }}>
                {editMetadata.map((pair, idx) => (
                  <div
                    key={`edit-meta-${idx}`}
                    style={{ display: 'grid', gridTemplateColumns: '1fr 1fr auto', gap: 8 }}
                  >
                    <input
                      className="form-input"
                      placeholder="Key"
                      value={pair.key}
                      onChange={e =>
                        setEditMetadata(rows =>
                          rows.map((r, i) => (i === idx ? { ...r, key: e.target.value } : r))
                        )
                      }
                    />
                    <input
                      className="form-input"
                      placeholder="Value"
                      value={pair.value}
                      onChange={e =>
                        setEditMetadata(rows =>
                          rows.map((r, i) => (i === idx ? { ...r, value: e.target.value } : r))
                        )
                      }
                    />
                    <button
                      type="button"
                      className="btn-secondary"
                      onClick={() =>
                        setEditMetadata(rows =>
                          rows.length > 1
                            ? rows.filter((_, i) => i !== idx)
                            : [{ key: '', value: '' }]
                        )
                      }
                    >
                      Remove
                    </button>
                  </div>
                ))}
              </div>
              <div style={{ marginTop: 8 }}>
                <button
                  type="button"
                  className="btn-secondary"
                  onClick={() => setEditMetadata(rows => [...rows, { key: '', value: '' }])}
                >
                  + Add metadata row
                </button>
              </div>
            </div>
            {/* ── Visits ── */}
            <div style={{ marginTop: 14 }}>
              <div style={{ fontSize: 11, fontFamily: "'DM Mono',monospace", color: 'var(--text3)', marginBottom: 8 }}>
                VISITS
              </div>
              {visitsLoading ? (
                <div style={{ fontSize: 13, color: 'var(--text2)' }}>Loading…</div>
              ) : visits.length === 0 ? (
                <div style={{ fontSize: 13, color: 'var(--text2)', marginBottom: 8 }}>No visits logged yet.</div>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginBottom: 8 }}>
                  {[...visits].reverse().map(v => (
                    <div
                      key={v.id}
                      style={{
                        background: 'var(--surface2)',
                        border: '1px solid var(--border)',
                        borderRadius: 10,
                        padding: '12px 14px',
                      }}
                    >
                      {editingVisitId === v.id ? (
                        <div>
                          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginBottom: 10 }}>
                            <div>
                              <label className="form-label">Date *</label>
                              <input
                                type="date"
                                className="form-input"
                                value={editVisitForm.date}
                                onChange={e => setEditVisitForm(f => ({ ...f, date: e.target.value }))}
                              />
                            </div>
                            <div>
                              <label className="form-label">Offering *</label>
                              <div style={{ position: 'relative' }}>
                                <select
                                  className="form-input"
                                  style={{ appearance: 'none', cursor: services.length === 0 ? 'not-allowed' : 'pointer', paddingRight: 32, opacity: services.length === 0 ? 0.6 : 1 }}
                                  value={editVisitForm.serviceId}
                                  onChange={e => setEditVisitForm(f => ({ ...f, serviceId: e.target.value }))}
                                  required
                                  disabled={services.length === 0}
                                >
                                  <option value="" disabled>{services.length === 0 ? 'No offerings — add one first' : 'Select an offering…'}</option>
                                  {services.map(svc => (
                                    <option key={svc.id} value={svc.id}>
                                      {svc.name}{svc.price != null ? ` — $${svc.price.toFixed(2)}` : ''}
                                    </option>
                                  ))}
                                </select>
                                <span style={{ position: 'absolute', right: 10, top: '50%', transform: 'translateY(-50%)', pointerEvents: 'none', fontSize: 11, color: 'var(--text2)' }}>▼</span>
                              </div>
                            </div>
                          </div>
                          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginBottom: 10 }}>
                            <div>
                              <label className="form-label">Staff Name</label>
                              <input
                                type="text"
                                maxLength={120}
                                className="form-input"
                                value={editVisitForm.staffName}
                                placeholder="e.g. Sarah"
                                onChange={e => setEditVisitForm(f => ({ ...f, staffName: e.target.value }))}
                              />
                            </div>
                            <div>
                              <label className="form-label">Total Amount</label>
                              <input
                                type="number"
                                step="0.01"
                                min="0"
                                className="form-input"
                                value={editVisitForm.totalAmount}
                                placeholder="e.g. 65.00"
                                onChange={e => setEditVisitForm(f => ({ ...f, totalAmount: e.target.value }))}
                              />
                            </div>
                          </div>
                          <div style={{ marginBottom: 10 }}>
                            <label className="form-label">Notes</label>
                            <textarea
                              rows={2}
                              maxLength={500}
                              className="form-input"
                              style={{ resize: 'vertical', fontFamily: 'inherit', lineHeight: 1.5 }}
                              value={editVisitForm.notes}
                              placeholder="Optional notes…"
                              onChange={e => setEditVisitForm(f => ({ ...f, notes: e.target.value }))}
                            />
                          </div>
                          {visitError && (
                            <div style={{ color: 'var(--red)', fontSize: 12, marginBottom: 8 }}>{visitError}</div>
                          )}
                          <div style={{ display: 'flex', gap: 8 }}>
                            <button
                              type="button"
                              className="btn-primary"
                              style={{ fontSize: 13 }}
                              onClick={() => void handleVisitSave(v.id)}
                              disabled={visitBusy}
                            >
                              {visitBusy ? 'Saving…' : 'Save'}
                            </button>
                            <button
                              type="button"
                              className="btn-secondary"
                              style={{ fontSize: 13 }}
                              onClick={() => { setEditingVisitId(null); setVisitError(null) }}
                              disabled={visitBusy}
                            >
                              Cancel
                            </button>
                          </div>
                        </div>
                      ) : (
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12 }}>
                          <div>
                            <div style={{ fontWeight: 500, color: 'var(--text)', fontSize: 14 }}>
                              {new Date(v.visitDate).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                            </div>
                            {v.serviceName && <div style={{ fontSize: 12, color: 'var(--text2)', marginTop: 2 }}>{v.serviceName}</div>}
                            {v.staffName && <div style={{ fontSize: 12, color: 'var(--text2)', marginTop: 2 }}>Staff: {v.staffName}</div>}
                            {v.notes && <div style={{ fontSize: 12, color: 'var(--text2)', marginTop: 4, fontStyle: 'italic' }}>{v.notes}</div>}
                          </div>
                          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 6 }}>
                            {v.totalAmount != null && (
                              <div style={{ fontWeight: 600, color: 'var(--text)', fontSize: 14, whiteSpace: 'nowrap' }}>
                                ${v.totalAmount.toFixed(2)}
                              </div>
                            )}
                            <div style={{ display: 'flex', gap: 6 }}>
                              <button
                                type="button"
                                className="btn-secondary"
                                style={{ fontSize: 12, padding: '3px 10px' }}
                                onClick={() => startEditVisit(v)}
                              >
                                Edit
                              </button>
                              <button
                                type="button"
                                className="btn-secondary"
                                style={{ fontSize: 12, padding: '3px 10px', color: 'var(--red)' }}
                                onClick={() => void handleVisitDelete(v.id)}
                                disabled={visitBusy}
                              >
                                Delete
                              </button>
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
              <button type="button" className="btn-secondary" onClick={() => setLogVisitOpen(true)}>
                + Log Visit
              </button>
            </div>

            {error ? (
              <div style={{ marginTop: 12, color: 'var(--red)', fontSize: 13 }}>{error}</div>
            ) : null}
          </form>
        )}
      </div>
    </div>
  )
}
